import React, { useEffect, useState } from "react";
import AggregatedValueBoard from "../AggregatedValueBoard";
import UnifiedSuccessBoard from "../UnifiedSuccessBoard";
import DirectIndirectView from "../DirectIndirectView";
import PerformanceLedger from "../PerformanceLedger";
import ComplianceAudit from "../ComplianceAudit";
import IntelligenceRouterFooter from "../IntelligenceRouterFooter";
import TeamPulseCard from "../TeamPulseCard";
import { api } from "../../lib/api";

/* ────────────────────────────────────────────────────────────────────────────
 * Common Operating Picture — definitive hierarchical layout
 *   1. Impact & Outcomes  · Hero KPI + Protective Impact
 *   2. Unified Success    · Four shared behavioral KPIs
 *   3. Strategic Portfolio · Direct vs Indirect Treemap
 *   4. Performance Ledger · Attestation chain + Sourcing levers
 *   5. Trust Foundation   · Compliance · Integrity · Audit
 * ──────────────────────────────────────────────────────────────────────── */

export default function StrategyTab({ openProof, openSourcing, onNavigate }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.directIndirect().then(setSummary).catch(() => setSummary(null)); }, []);

  return (
    <div className="space-y-6" data-testid="tab-strategy-content">
      {/* 0. Team Pulse — 4-tier hierarchy integrated into the COP */}
      <section data-testid="row-team-pulse">
        <TeamPulseCard onOpenTeam={() => onNavigate && onNavigate("team")} />
      </section>

      {/* 1. Unified Success — shared partnership KPIs FIRST (no preamble) */}
      <section data-testid="row-unified-success">
        <UnifiedSuccessBoard showProtectiveImpact={false} />
      </section>

      {/* 2. Impact & Outcomes — Hero banner with Protective Impact integrated */}
      <section data-testid="row-impact-outcomes" className="space-y-3">
        <SectionRail index="02" title="Impact & Outcomes" subtitle="Outcome-as-a-Service · realized + protective impact" />
        <AggregatedValueBoard summary={summary} onNavigate={onNavigate} />
      </section>

      {/* 3. Strategic Portfolio — Treemap */}
      <section data-testid="row-strategic-portfolio" className="space-y-3">
        <SectionRail index="03" title="Strategic Portfolio" subtitle="Direct vs Indirect · UNSPSC orientation map" />
        <DirectIndirectView summary={summary} />
      </section>

      {/* 4. Savings Audit & Verification */}
      <section data-testid="row-performance-ledger" className="space-y-3">
        <SectionRail index="04" title="Savings Audit & Verification" subtitle="Decision Proof · Should-Cost · Rate Benchmark" />
        <PerformanceLedger
          onOpenSavingsProof={(r) => openProof("SAVINGS", r)}
          onOpenSourcingLever={(mode, r) => openSourcing(mode, r)}
        />
      </section>

      {/* 5. Governance & Compliance — Compliance + Integrity */}
      <section data-testid="row-trust-foundation" className="space-y-3">
        <SectionRail index="05" title="Governance & Compliance" subtitle="SOC 2 controls · Integrity assertions · Investigations" />
        <ComplianceAudit />
      </section>

      <IntelligenceRouterFooter />
    </div>
  );
}

function SectionRail({ index, title, subtitle }) {
  return (
    <header className="flex items-baseline gap-4 px-1">
      <span className="font-mono text-[10px] text-slate-500 tracking-mono-label">
        {index}
      </span>
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="font-display text-[15px] font-semibold text-slate-100 leading-none">
          {title}
        </h2>
        <span className="font-mono text-[11px] text-slate-500">· {subtitle}</span>
      </div>
    </header>
  );
}
